<template>
  <!-- The page scrolls inside this box rather than the window, so nothing behind a fixed layer can scroll -->
  <div
    ref="scroller"
    class="h-[100dvh] w-full overscroll-contain bg-gray-100"
    :class="locked ? 'overflow-hidden' : 'overflow-y-auto'"
    style="padding-bottom: calc(4rem + env(safe-area-inset-bottom))"
    @scroll="onScroll"
  >
    <div v-if="!isOnline" class="sticky top-0 z-50 bg-red-100 text-red-700 text-sm text-center py-1.5">You're offline</div>
    <div ref="content">
      <slot></slot>
    </div>
    <button
      v-if="showScrollUp"
      class="fixed left-1/2 -translate-x-1/2 top-4 z-30 flex items-center gap-1 px-3 py-1.5 rounded-full border-[0.5px] border-dark-primary/15 bg-white/90 shadow text-sm text-dark-primary"
      @click="scroller.scrollTo({ top: 0, behavior: 'smooth' })"
    ><ChevronUpIcon class="h-4 w-4" />Scroll to Top</button>
    <button
      v-if="showScrollDown"
      class="fixed left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 px-3 py-1.5 rounded-full border-[0.5px] border-dark-primary/15 bg-white/90 shadow text-sm text-dark-primary"
      style="bottom: calc(4.5rem + env(safe-area-inset-bottom))"
      @click="scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' })"
    ><ChevronDownIcon class="h-4 w-4" />Scroll to Bottom</button>
  </div>
</template>

<script setup>
import { ref, defineProps, defineExpose, onActivated } from 'vue';
import { useOnline, useResizeObserver } from '@vueuse/core';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/20/solid';

defineProps({ locked: Boolean })

const isOnline = useOnline()

const scroller = ref(null)
const content = ref(null)
const showScrollUp = ref(false)
const showScrollDown = ref(false)
// Down while in the top half of the page, up while in the bottom half. Content loads
// and grows without scrolling, so this also runs when its size changes
function updateScrollButtons() {
  const el = scroller.value
  const maxScroll = el.scrollHeight - el.clientHeight
  const scrollable = maxScroll > 50
  showScrollUp.value = scrollable && el.scrollTop > maxScroll / 2
  showScrollDown.value = scrollable && el.scrollTop <= maxScroll / 2
}
useResizeObserver(content, () => updateScrollButtons())

// Kept-alive pages lose the box's scroll position while hidden, so restore it when shown again
let scrollTop = 0
function onScroll() {
  // Hiding the box resets it to 0, which isn't a position worth keeping
  if (scroller.value.clientHeight === 0) return
  scrollTop = scroller.value.scrollTop
  updateScrollButtons()
}
onActivated(() => {
  if (scroller.value) scroller.value.scrollTop = scrollTop
})

defineExpose({
  scrollToTop() { scroller.value.scrollTop = 0 },
})

// iOS zooms into inputs under 16px when focused
document.querySelector('meta[name="viewport"]').setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1')
</script>

<style>
/* PWA toasts use position 'bottom', which desktop never uses: smaller, and just above the nav bar */
.v-toast:has(.v-toast__item--bottom) {
  padding: 0 1rem calc(4.5rem + env(safe-area-inset-bottom));
}
.v-toast__item--bottom {
  min-height: 0;
  font-size: 0.875rem;
}
.v-toast__item--bottom .v-toast__text {
  padding: 0.5rem 0.75rem;
}
.v-toast__item--bottom .v-toast__icon {
  width: 18px;
  min-width: 18px;
  height: 18px;
  margin-left: 0.75rem;
}
</style>
